import React, { useState, useEffect } from 'react';
import './CodeTerminal.css';

const highlightCode = (line) => {
  if (!line.trim()) return line;
  
  let text = line;
  
  // Match patterns and create spans
  const patterns = [
    { regex: /\b(const|let|var|function|class|if|else|switch|case|return|async|await|new|this|Math|parseFloat|parseInt)\b/g, className: 'code-keyword' },
    { regex: /(['"`])((?:\\.|(?!\1)[^\\])*?)\1/g, className: 'code-string' },
    { regex: /\b(\d+\.?\d*)\b/g, className: 'code-number' },
    { regex: /\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*(?=\()/g, className: 'code-function' },
    { regex: /\/\/.*$/g, className: 'code-comment' }
  ];
  
  const matches = [];
  patterns.forEach(({ regex, className }) => {
    let match;
    regex.lastIndex = 0;
    while ((match = regex.exec(text)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        text: match[0],
        className
      });
    }
  });
  
  matches.sort((a, b) => a.start - b.start);
  
  let result = [];
  let currentIndex = 0;
  
  matches.forEach(match => {
    if (match.start > currentIndex) {
      result.push(text.substring(currentIndex, match.start));
    }
    result.push(
      <span key={`${match.start}-${match.end}`} className={match.className}>
        {match.text}
      </span>
    );
    currentIndex = match.end;
  });
  
  if (currentIndex < text.length) {
    result.push(text.substring(currentIndex));
  }
  
  return result.length > 0 ? result : text;
};

const CodeTerminal = ({ code, speed = 30, onComplete }) => {
  const [displayedCode, setDisplayedCode] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const terminalBodyRef = React.useRef(null);

  useEffect(() => {
    // Reset when code changes
    setDisplayedCode('');
    setCurrentIndex(0);
    setIsTyping(true);
  }, [code]);

  useEffect(() => {
    if (currentIndex < code.length) {
      const timeout = setTimeout(() => {
        setDisplayedCode(prev => prev + code[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);

      return () => clearTimeout(timeout);
    } else {
      setIsTyping(false);
      if (onComplete) {
        setTimeout(() => onComplete(), 100);
      }
    }
  }, [currentIndex, code, speed, onComplete]);

  // Auto-scroll to bottom when content changes
  useEffect(() => {
    const scrollToBottom = () => {
      if (terminalBodyRef.current) {
        const element = terminalBodyRef.current;
        // Scroll to bottom to show the cursor
        element.scrollTop = element.scrollHeight;
      }
    };

    // Use requestAnimationFrame to ensure DOM is updated after render
    const rafId = requestAnimationFrame(() => {
      scrollToBottom();
      // Double-check after a microtask to catch any late updates
      setTimeout(scrollToBottom, 0);
    });

    return () => cancelAnimationFrame(rafId);
  }, [displayedCode, isTyping]);

  return (
    <div className="code-terminal">
      <div className="terminal-header">
        <div className="terminal-buttons">
          <span className="terminal-button red"></span>
          <span className="terminal-button yellow"></span>
          <span className="terminal-button green"></span>
        </div>
        <div className="terminal-title">nosoul@terminal ~/protocol</div>
      </div>
      <div className="terminal-body" ref={terminalBodyRef}>
        <div className="code-lines">
          {displayedCode.split('\n').map((line, idx, arr) => (
            <div key={idx} className="code-line">
              {idx === 0 && <span className="code-prompt">$</span>}
              <span className="code-content">
                {highlightCode(line)}
                {idx === arr.length - 1 && isTyping && (
                  <span className="cursor">▊</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CodeTerminal;
